import { useMemo } from 'react'
import { ChainId } from 'symbiosis-js-sdk'
import { Web3Provider } from '@ethersproject/providers'
import { useAccount, useSwitchChain, useConnectorClient } from 'wagmi'

import { WalletHook, WalletStatus } from './types'

function clientToWeb3Provider(client: {
	chain: { id: number; name: string }
	transport: unknown
}): Web3Provider {
	const { chain, transport } = client
	const network = {
		chainId: chain.id,
		name: chain.name,
	}
	return new Web3Provider(transport as any, network)
}

export const useWallet = (): WalletHook => {
	const { address, status: wagmiStatus, chainId } = useAccount()
	const { data: connectorClient } = useConnectorClient()
	const { switchChainAsync } = useSwitchChain()

	const provider = useMemo(() => {
		if (!connectorClient) return null
		return clientToWeb3Provider(connectorClient)
	}, [connectorClient])

	const signer = useMemo(() => {
		if (!provider || !address) return null
		return provider.getSigner(address)
	}, [provider, address])

	const status = useMemo(() => {
		if (wagmiStatus === 'connected') return WalletStatus.CONNECTED
		if (wagmiStatus === 'connecting' || wagmiStatus === 'reconnecting')
			return WalletStatus.CONNECTING
		return WalletStatus.DISCONNECTED
	}, [wagmiStatus])

	const setChain = async (targetChainId: ChainId): Promise<void> => {
		await switchChainAsync({ chainId: targetChainId })
	}

	const isCurrentChain = (targetChainId: ChainId): boolean => {
		return chainId === targetChainId
	}

	return {
		signer,
		account: address ?? null,
		chainId: chainId as ChainId,
		status,
		setChain,
		isCurrentChain,
	}
}
